<template>
  <div>
    <ModalDialog
      ref="dialog"
      title="Select Glass"
      :showSubmit="false"
      :scrollable="true"
      icon="fa-layer-group"
      @closed="onClosed">

      <Spinner v-if="loading" />
      <div v-if="!loading && stockData">
        <div>
          <ActionDropMenu class="float-right">
            <DropdownMenuItem @click="showEmptyGlassLevels = true" icon="fa-eye" v-if="!showEmptyGlassLevels">Show Empty Levels</DropdownMenuItem>
            <DropdownMenuItem @click="showEmptyGlassLevels = false" icon="fa-eye-slash" v-if="showEmptyGlassLevels">Hide Empty Levels</DropdownMenuItem>
          </ActionDropMenu>
          <ContentTitle>Select the glass stock size</ContentTitle>
        </div>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Size (HxW) mm</th>
              <th>Level Size</th>
              <th>Blocks</th>
              <th>Total Sheets</th>
              <th><Tooltip label="Stock sheets required for active orders" helpStyle>Stock Sheets Ordered</Tooltip></th>
              <th><Tooltip label="Stock allocated to optimiser sheets" helpStyle>Optimiser Sheets</Tooltip></th>
              <th><Tooltip label="Sheets remaining after stock orders and optimisation" helpStyle>Available</Tooltip></th>
              <th><Tooltip label="Incoming stock on order via purchase orders" helpStyle>Incoming</Tooltip></th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <template v-for="glassSize in availableGlassSizeList">
              <tr :key="glassSize.id">
                <td class="font-weight-bold">{{glassSize.height}} x {{glassSize.width}}</td>
                <td></td>
                <td class="font-weight-bold">{{glassSize.summary.totalBlocks}}</td>
                <td class="font-weight-bold">{{glassSize.summary.totalSheetStock - glassSize.summary.totalOffcuts}}</td>
                <td class="font-weight-bold">
                  <a @click.prevent="displayGlassOrderedPopover($event, glassSize.id)" href="#" class="link-select-options">
                    {{glassSize.summary.totalOrderEntryStock - glassSize.summary.totalOffcutOrderEntryStock}}
                  </a>
                </td>
                <td class="font-weight-bold">{{glassSize.summary.totalOptimiserAllocated - glassSize.summary.totalOffcutOptimiserAllocated}}</td>
                <td class="font-weight-bold">{{glassSize.summary.totalAvailable - glassSize.summary.totalOffcutAvailable}}</td>
                <td class="font-weight-bold">
                  <a @click.prevent="displayGlassPOPopover($event, glassSize.id)" href="#" class="link-select-options">
                    {{glassSize.summary.totalOnOrder}}
                  </a>
                </td>
                <td></td>
              </tr>
              <template v-for="glassLevel in filteredGlassLevelList(glassSize)">
                <tr :key="'level-' + glassLevel.id" v-if="!glassLevel.isOffcut">
                  <td></td>
                  <td v-if="glassLevel.isOffcut" :class="{'stock-glass-level-offcut': glassLevel.isOffcut}">
                    <Tooltip label="Offcut">
                      {{glassLevel.height}} x {{glassLevel.width}}
                    </Tooltip>
                  </td>
                  <td v-else>
                    {{glassLevel.height}} x {{glassLevel.width}}
                  </td>
                  <td>{{glassLevel.summary.totalBlocks}}</td>
                  <td>{{glassLevel.summary.totalSheetStock}}</td>
                  <td>{{glassLevel.summary.totalOrderEntryStock}}</td>
                  <td>{{glassLevel.summary.totalOptimiserAllocated}}</td>
                  <td>{{glassLevel.summary.totalAvailable}}</td>
                  <td>{{glassLevel.summary.totalOnOrder}}</td>
                  <td>
                    <button class="btn btn-sm btn-white" @click.prevent="selectGlassLevel(glassLevel)"><i class="far fa-check-circle"></i> Select</button>
                  </td>
                </tr>
              </template>
            </template>
          </template>
        </StaticDataTable>
        <p class="text-center font-italic" v-if="availableGlassSizeList.length == 0">No glass levels found</p>
        
        <FormHorizontalRule />

        <div class="mt-2">
          <h3>Offcuts</h3>
          <StaticDataTable class="static-table-list">
            <template v-slot:header>
              <tr>
                <th>Size (HxW) mm</th>
                <th>Barcode</th>
                <th v-if="isStockUsingGrain">Grain</th>
                <th><Tooltip label="Stock sheets required for active orders" helpStyle>Stock Sheets Ordered</Tooltip></th>
                <th><Tooltip label="Stock allocated to optimiser sheets" helpStyle>Optimiser Sheets</Tooltip></th>
                <th><Tooltip label="Sheets remaining after stock orders and optimisation" helpStyle>Available</Tooltip></th>
                <th>Bin</th>
                <th>Actions</th>
              </tr>
            </template>
            <template v-slot:body>
              <template v-for="glassSize in stockData.glassSizeList">
                <template v-for="glassLevel in glassSize.glassLevels">
                  <tr :key="'level-offcut-' + glassLevel.id" v-if="glassLevel.isOffcut && glassLevel.isOffcutAvailable">
                    <td class="font-weight-bold">{{glassLevel.height}} x {{glassLevel.width}}</td>
                    <td>{{glassLevel.barcode}}</td>
                    <td v-if="isStockUsingGrain">{{formatLookupName(glassSize.grainDirection, StockGrainDirectionTypeLookup)}}</td>
                    <td>{{glassLevel.summary.totalOrderEntryStock}}</td>
                    <td>{{glassLevel.summary.totalOptimiserAllocated}}</td>
                    <td>{{glassLevel.summary.totalAvailable}}</td>
                    <td>{{glassLevel.inventoryBin ? glassLevel.inventoryBin.code : '-'}}</td>
                    <td>
                      <button class="btn btn-sm btn-white" @click.prevent="selectGlassLevel(glassLevel)" v-if="glassLevel.summary.totalAvailable > 0"><i class="far fa-check-circle"></i> Select</button>
                    </td>
                  </tr>
                </template>
              </template>
            </template>
          </StaticDataTable>
          <p class="text-center font-italic" v-if="stockData.glassOffcutList.length == 0">No offcuts found</p>
        </div>
      </div>
    </ModalDialog>
    <GlassSizeOrderedPopover ref="glassOrderedPopover" /> 
    <GlassSizePOPopover ref="glassPOPopover" /> 
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import StaticDataTable from '@/components/table/staticDataTable.vue'
import GlassSizeOrderedPopover from '@/modules/inventory/components/glassSizeOrderedPopover'
import GlassSizePOPopover from '@/modules/inventory/components/glassSizePOPopover'

import { StockGrainDirectionTypeLookup } from '@/store/types'

export default {
  name: 'OrderSelectGlassLevelDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    StaticDataTable,
    GlassSizeOrderedPopover,
    GlassSizePOPopover
  },
  props: {
    stockId: Number
  },
  data() {
    return {
      dialogForm: {},
      loading: false,
      stockData: null,
      showEmptyGlassLevels: false
    }
  },
  mounted() {
    this.requestData()
  },
  computed: {
    availableGlassSizeList() {
      if(!this.stockData)
        return []
      return this.stockData.glassSizeList.filter(glassSize => glassSize.isActive)
    },
    isStockUsingGrain() {
      if(!this.stockData)
        return false
      return this.stockData.isStockUsingGrain
    },
  },
  created() {
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  methods: {
    show() {
      this.dialogForm = { }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    selectGlassLevel(glassLevel) {

      var glassLevelInfo = {
        id: glassLevel.id,
        width: glassLevel.width,
        height: glassLevel.height,
        isOffcut: glassLevel.isOffcut
      }

      this.modalClosed(glassLevelInfo)
      this.hide()
    },
    filteredGlassLevelList(glassSize) {
      if(this.showEmptyGlassLevels)
        return glassSize.glassLevels

      // Hide all levels have zero values for all fields
      return glassSize.glassLevels.filter(level => {
        // If no values, hide it
        if(level.summary.totalBlocks == 0 &&
           level.summary.totalSheetStock == 0 &&
           level.summary.totalOrderEntryStock == 0 &&
           level.summary.totalOptimiserAllocated == 0 &&
           level.summary.totalAvailable == 0 &&
           level.summary.totalOnOrder == 0) {
          return false
        }

        return true
      })
    },

    requestData() {
      this.loading = true

      this.$apiService.inventoryService.getStock(this.stockId)
      .then(response => {
        this.$data.stockData = response.data
      })
      .finally(() => {
        this.loading = false
      })
    },

    displayGlassOrderedPopover(evt, glassSizeId) {
      this.$refs.glassOrderedPopover.displayFromElement(evt.target, this.stockId, glassSizeId)
    },
    displayGlassPOPopover(evt, glassSizeId) {
      this.$refs.glassPOPopover.displayFromElement(evt.target, this.stockId, glassSizeId)
    },
  }
}
</script>